'use client'

import { useCallback, useMemo } from 'react'

import { isEnableFlagUnset } from '@/features/order/order-form/lib/enable-flag'
import { hasTriggerData, isTriggerActive } from '@/features/order/order-form/lib/trigger-active'
import { triggerSummaryLabel } from '@/features/order/order-form/lib/trigger-summary'
import type { OrderFormCollapsibleConfig } from '@/state/order'

// Wipes every field hasTriggerData() inspects, plus expiry, and re-arms auto-on
// by clearing the enable flag back to its unset state.
const ENTRY_CLEAR_PATCH: Partial<OrderFormCollapsibleConfig> = {
  entryConditionTreeJson: '',
  entryConditionGroupJson: '',
  entryConditionPrice: '',
  // Reset the legacy flat fields too, otherwise legacyConfigToTree can rebuild a
  // stale partial node (e.g. an instrument/price-ref) after the tree is cleared.
  entryConditionInstrument: '',
  entryConditionPriceRef: '',
  entryConditionOperator: 'equal_to',
  entryConditionTriggerType: 'price',
  entryConditionDelaySeconds: '',
  entryConditionFireAtMs: '',
  entryConditionScheduleVariant: 'delay',
  entryConditionActionEventType: '',
  entryConditionActionTargetType: 'order_id',
  entryConditionActionTargetValue: '',
  entryConditionActionPartialFillThreshold: '',
  entryConditionExpiryMinutes: '',
  entryConditionLogic: 'single',
  entryConditionEnabled: '',
}

// Shared derived state + actions for the Order Trigger section,
// used by both the inline collapsible area (CollapsibleAreaInOrderForm) and the
// expanded sheet's detail panel (OrderFormDetailPanel) so the two never diverge.
export function useOrderFormCollapsibleControls(
  config: Required<OrderFormCollapsibleConfig>,
  updateConfig: (patch: Partial<OrderFormCollapsibleConfig>) => void
) {
  const { triggerActive, triggerHasData, triggerLabel } = useMemo(
    () => ({
      triggerActive: isTriggerActive(config),
      triggerHasData: hasTriggerData(config),
      triggerLabel: triggerSummaryLabel(config),
    }),
    [config]
  )

  // Auto-arm the trigger the first time the user edits it, but never override an
  // explicit on/off choice they've already made.
  const editEntryConfig = useCallback(
    (patch: Partial<OrderFormCollapsibleConfig>) => {
      if (isEnableFlagUnset(config.entryConditionEnabled)) {
        updateConfig({ ...patch, entryConditionEnabled: 'true' })
      } else {
        updateConfig(patch)
      }
    },
    [config.entryConditionEnabled, updateConfig]
  )

  const setTriggerEnabled = useCallback(
    (on: boolean) => updateConfig({ entryConditionEnabled: on ? 'true' : 'false' }),
    [updateConfig]
  )

  const clearTrigger = useCallback(() => updateConfig(ENTRY_CLEAR_PATCH), [updateConfig])

  return {
    triggerActive,
    triggerHasData,
    triggerLabel,
    editEntryConfig,
    setTriggerEnabled,
    clearTrigger,
  }
}
